// profileHelpers
import templatePt from "../data/pt/character_templates.json";
import templateEn from "../data/en/character_templates.json";

export function computeInsights(traits, archetypeOverride = null, lang) {
  const [
    emotionality,
    activity,
    sociability,
    discipline,
    mentalOpenness,
    emotionalOpenness,
    perspective,
  ] = traits.map((v) => (v === undefined ? null : Number(v)));

  const template = lang?.startsWith("pt") ? templatePt : templateEn;

  if (!traits || !Array.isArray(traits)) {
    console.warn(
      "computeInsights: 'traits' inválido ou faltando. Usando fallback neutro [50,50,50,50,50,50,50].",
      traits
    );
    traits = [50, 50, 50, 50, 50, 50, 50];
  }

  const classify = (v) => (v > 60 ? "high" : v >= 40 ? "mid" : "low");

  const Ecls = classify(emotionality || 0);
  const Acls = classify(activity || 0);
  const Scls = classify(sociability || 0);
  const DiscCls = classify(discipline || 0);
  const MindCls = classify(mentalOpenness || 0);
  const EmoCls = classify(emotionalOpenness || 0);
  const PerspCls = classify(perspective || 0);

  // fallback to a minimal object to avoid crashes
  const archetype = archetypeOverride || {
    label: "—",
    key: null,
    emoji: "🧭",
    description: "",
  };

  const temperamentStrengths = [];
  if (Acls === "high")
    temperamentStrengths.push(
      "Tende a iniciar ações com energia e propensão para movimento."
    );
  if (Acls === "mid")
    temperamentStrengths.push("Combina ação com reflexão — flexível em ritmo.");
  if (Acls === "low")
    temperamentStrengths.push(
      "Economiza energia e tende a planejar antes de agir."
    );

  if (Ecls === "high")
    temperamentStrengths.push(
      "Maior sensibilidade emocional — bom para empatia e expressão."
    );
  if (Ecls === "mid")
    temperamentStrengths.push(
      "Emoções moderadas: equilíbrio entre reatividade e calma."
    );
  if (Ecls === "low")
    temperamentStrengths.push(
      "Estabilidade emocional — menos vulnerável a oscilações momentâneas."
    );

  if (Scls === "high")
    temperamentStrengths.push(
      "Busca conexão social — facilita apoio e aprendizagem em grupo."
    );
  if (Scls === "mid")
    temperamentStrengths.push(
      "Confortável tanto com interação quanto com atividades solitárias."
    );
  if (Scls === "low")
    temperamentStrengths.push(
      "Prefere trabalho individual e ambientes mais silenciosos — bom para concentração profunda."
    );

  const characterResources = [];
  if (DiscCls === "low")
    characterResources.push(
      "Trabalhar pequenas rotinas de 2–5 minutos para ganhar previsibilidade."
    );
  else if (DiscCls === "mid")
    characterResources.push(
      "Estabilizar rituais e usar microplanejamento em momentos de demanda."
    );
  else
    characterResources.push(
      "Aprimorar priorização e delegação para evitar rigidez excessiva."
    );

  if (MindCls === "low")
    characterResources.push(
      "Exercícios simples de perspectiva (3 alternativas) e desfusão cognitiva."
    );
  else if (MindCls === "mid")
    characterResources.push(
      "Práticas para aumentar a flexibilidade de pensamento diante de problemas novos."
    );
  else
    characterResources.push(
      "Aproveitar sua abertura mental para testar abordagens variadas e documentar aprendizados."
    );

  if (EmoCls === "low")
    characterResources.push(
      "Exposição emocional gradual e exercícios de aceitação de sensações."
    );
  else if (EmoCls === "mid")
    characterResources.push(
      "Expandir tolerância emocional com práticas curtas de presença."
    );
  else
    characterResources.push(
      "Usar sua abertura emocional para práticas de aceitação e ação baseada em valores."
    );

  if (PerspCls === "low")
    characterResources.push(
      "Exercícios de self-observação e práticas breves de presença (3 minutos)."
    );
  else if (PerspCls === "mid")
    characterResources.push(
      "Práticas regulares de mindfulness para consolidar auto-observação."
    );
  else
    characterResources.push(
      "Use sua perspectiva para revisar escolhas e aumentar distância saudável de pensamentos e emoções."
    );

  const temperamentLabels = ["Emocionalidade", "Atividade", "Sociabilidade"];
  const characterLabels = [
    "Disciplina",
    "Abertura Mental",
    "Abertura Emocional",
    "Perspectiva",
  ];

  const temperValues = {
    Emocionalidade: Math.round(emotionality || 0),
    Atividade: Math.round(activity || 0),
    Sociabilidade: Math.round(sociability || 0),
  };
  const charValues = {
    Disciplina: Math.round(discipline || 0),
    "Abertura Mental": Math.round(mentalOpenness || 0),
    "Abertura Emocional": Math.round(emotionalOpenness || 0),
    Perspectiva: Math.round(perspective || 0),
  };

  // Build integration matrix
  const integrationMatrix = [];
  for (const t of temperamentLabels) {
    for (const c of characterLabels) {
      const text = interactionText(t, temperValues[t], c, charValues[c]);
      integrationMatrix.push({
        temper: t,
        character: c,
        text,
        temperVal: temperValues[t],
        charVal: charValues[c],
      });
    }
  }

  const characterCols = characterLabels.map((label) => ({
    label,
    value: charValues[label],
  }));
  const temperRows = temperamentLabels.map((label) => ({
    label,
    value: temperValues[label],
  }));

  const recommendations = generateRecommendations({
    emotionality,
    activity,
    sociability,
    discipline,
    mentalOpenness,
    emotionalOpenness,
    perspective,
  });

  // choose lowest character trait with tie-breaker:
  // tie-breaker order: Abertura Emocional -> Disciplina -> Abertura Mental -> Perspectiva
  const charValuesArr = [
    {
      key: "discipline",
      label: template.discipline.label,
      value: discipline || 0,
      description: template.discipline.description,
    },
    {
      key: "mind",
      label: template.mind.label,
      value: mentalOpenness || 0,
      description: template.mind.description,
    },
    {
      key: "emotion",
      label: template.emotion.label,
      value: emotionalOpenness || 0,
      description: template.emotion.description,
    },
    {
      key: "perspective",
      label: template.perspective.label,
      value: perspective || 0,
      description: template.perspective.description,
    },
  ];

  // find the minimum value
  const minVal = Math.min(...charValuesArr.map((c) => c.value));
  // tie breaker priority: emotion, discipline, mind, perspective
  const tiePriority = ["emotion", "discipline", "mind", "perspective"];
  const candidates = charValuesArr.filter((c) => c.value === minVal);
  let chosenChar;
  if (candidates.length === 1) {
    chosenChar = candidates[0];
  } else {
    // select based on tiePriority order
    chosenChar =
      tiePriority
        .map((k) => candidates.find((c) => c.key === k))
        .find(Boolean) || candidates[0];
  }

  const miniPlan = make7DayPlan(chosenChar.key);
  const weeklyQuestion = makeWeeklyQuestion(chosenChar.key, archetype.label);

  return {
    archetype,
    temperamentStrengths,
    characterResources,
    integrationMatrix,
    temperamentLabels,
    characterLabels,
    characterCols,
    temperRows,
    recommendations,
    miniPlan,
    weeklyQuestion,
    chosenCharacter: chosenChar, // expose which char was chosen
  };
}

// Interaction text generator: produces practical text that explains how one temp trait impacts one char trait
export function interactionText(temper, temperVal, character, charVal) {
  // temperVal and charVal: 0..100
  const tHigh = temperVal > 65;
  const tMid = temperVal >= 40 && temperVal <= 65;
  const tLow = temperVal < 40;

  const cHigh = charVal > 65;
  const cMid = charVal >= 40 && charVal <= 65;
  const cLow = charVal < 40;

  // Emocionalidade -> Disciplina / Mental / Emocional / Perspectiva
  if (temper === "Emocionalidade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Emoções intensas podem gerar ciclos de ação impulsiva que prejudicam a disciplina. Micro-rotinas de 2 minutos ajudam a segurar o impulso e criar previsibilidade.";
    if (tHigh && cMid)
      return "Sua sensibilidade pode ser aliada: reconheça momentos emocionais e use um checkpoint simples (ex.: 2 minutos) antes de decidir ações importantes.";
    if (tLow && cLow)
      return "Baixa reatividade emocional e pouca disciplina podem levar a procrastinação silenciosa — defina sinais externos (alarme) para iniciar tarefas.";
    if (tLow && cHigh)
      return "Estabilidade emocional combinada com boa disciplina favorece execução calma e consistente.";
    return "Emoções influenciam a clareza de prioridades; observar isso permite ajustar rotinas conforme o estado interno.";
  }

  if (temper === "Emocionalidade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Alto impacto emocional com pouca abertura mental tende a reforçar interpretações únicas dos eventos; perguntas de curiosidade (3 alternativas) ajudam a diluir crenças automáticas.";
    if (tHigh && cHigh)
      return "Sentir intensamente e ter mente aberta é ótimo: use reflexão guiada para transformar emoção em novas perspectivas.";
    if (tLow && cLow)
      return "Pouca emoção e pouca abertura pode levar a apatia cognitiva; experimente pequenos exercícios criativos para ativar outra leitura da situação.";
    return "Emoções afetam a forma como interpretamos informação; cultivar curiosidade beneficia a regulação emocional.";
  }

  if (temper === "Emocionalidade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Quando emoções são intensas e a abertura é baixa, é comum evitar sentimentos. Práticas graduais de aceitação (1–2 min) são úteis.";
    if (tHigh && cHigh)
      return "Alta sensibilidade com abertura emocional permite explorar experiências internas com apoio — foque em ações valiosas mesmo em presença de desconforto.";
    if (tLow && cLow)
      return "Pouca emoção e pouca abertura podem indicar esquiva: experimentar sentir pequenas emoções escolhidas pode ampliar repertório emocional.";
    return "Abertura emocional modula como a intensidade emocional se traduz em ação. Treinos curtos costumam ter bom custo/benefício.";
  }

  if (temper === "Emocionalidade" && character === "Perspectiva") {
    if (tHigh && cLow)
      return "Emoções intensas com baixa perspectiva geram identificação forte com o conteúdo emocional; práticas de self-como-contexto (observar sem ser definido) são úteis.";
    if (tHigh && cHigh)
      return "Alta emoção e boa perspectiva permitem sentir sem ser dominado — aproveite para praticar presença curta antes de agir.";
    if (tLow && cLow)
      return "Baixa emoção e baixa perspectiva podem gerar rigidez de identidade — experimente exercícios que ampliem 'eu como contexto' por 2-3 minutos.";
    return "Perspectiva ajuda a separar sensação do eu narrativo; fortalecê-la amplia escolhas diante de emoções.";
  }

  // Atividade -> Disciplina / Mental / Emocional / Perspectiva
  if (temper === "Atividade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Energia alta com pouca disciplina tende a gerar início de muitas tarefas e pouca conclusão. Use timers curtos e checkpoints para transformar impulso em resultados.";
    if (tHigh && cHigh)
      return "Alta energia + boa disciplina é poderosa: planeje blocos de ação e reserve tempo de recuperação.";
    if (tLow && cLow)
      return "Baixa energia e pouca disciplina dificultam iniciar tarefas; comece com microtarefas de 5 minutos e celebre pequenas conclusões.";
    return "A combinação entre energia e disciplina define velocidade e sustentabilidade das ações — pequenos ajustes na rotina geram impacto grande.";
  }

  if (temper === "Atividade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Atividade elevada com mente fechada pode resultar em ações repetitivas sem testar alternativas; proponha experimentos curtos para validar novas abordagens.";
    if (tHigh && cHigh)
      return "Energia e mente aberta favorecem exploração ágil: faça pequenos testes e registre aprendizados.";
    if (tLow && cLow)
      return "Baixa energia e pouca abertura tornam a exploração de novas ideias mais difícil — comece por anotar uma alternativa por dia.";
    return "A energia disponível influencia a disposição para experimentar novas ideias; alinhe experimentos ao seu nível de energia.";
  }

  if (temper === "Atividade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Muita ação com pouca abertura emocional pode levar a evitar sentir — considere pausas curtas para checar sensações antes de agir.";
    if (tHigh && cHigh)
      return "Alta energia e abertura emocional permitem agir mesmo com emoções presentes — canalize impulsos para ações valiosas.";
    if (tLow && cLow)
      return "Baixa energia e pouca abertura podem manter a evitação; inicie com exercícios breves de presença antes de agir.";
    return "Energia e abertura emocional juntas determinam se você age apesar do desconforto ou age para evitá-lo.";
  }

  if (temper === "Atividade" && character === "Perspectiva") {
    if (tHigh && cLow)
      return "Alta atividade com baixa perspectiva pode levar a ações automáticas sem observar o 'quem age'; inserir pausas de 10-20s amplia consciência.";
    if (tHigh && cHigh)
      return "Alta energia com boa perspectiva permite agir com consciência — pratique checar intenções antes de executar ações rápidas.";
    if (tLow && cLow)
      return "Baixa energia e baixa perspectiva podem criar sensação de piloto automático; exercícios curtos de presença ajudam a iniciar com intenção.";
    return "Perspectiva regula a consciência antes da ação; treiná-la melhora a qualidade das escolhas.";
  }

  // Sociabilidade -> Disciplina / Mental / Emocional / Perspectiva
  if (temper === "Sociabilidade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Alto desejo por conexão com baixa disciplina pode favorecer priorizar relações sobre tarefas; usar acordos sociais (com alguém) como compromisso aumenta adesão.";
    if (tHigh && cHigh)
      return "Sociabilidade e disciplina permitem coordenar equipes e manter resultados pelo suporte social.";
    if (tLow && cLow)
      return "Preferência por trabalho solo somada à pouca disciplina pode gerar isolamento produtivo ou desorganização — definir rituais de revisão ajuda.";
    return "Relações influenciam rotinas: incorporar suporte social em checklists aumenta consistência.";
  }

  if (temper === "Sociabilidade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Ser sociável mas pouco aberto mentalmente pode cristalizar crenças de grupo; exponha-se a ideias diversas em conversa estruturada (curiosidade ativa).";
    if (tHigh && cHigh)
      return "Sociabilidade e mente aberta criam terreno fértil para aprender com outros — busque discussões com diversidade de opiniões.";
    if (tLow && cLow)
      return "Pessoas mais reservadas com mente fechada podem perder feedback valioso — experimente compartilhar uma hipótese e pedir 2 interpretações.";
    return "O modo como você se relaciona com os outros altera as oportunidades de ampliar perspectivas.";
  }

  if (temper === "Sociabilidade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Gosta de estar com pessoas mas evita aprofundar emoções — tente ensaiar partilhas curtas (30–60s) com alguém de confiança.";
    if (tHigh && cHigh)
      return "Sociabilidade com abertura emocional favorece construir vínculos autênticos — pratique vulnerabilidade gradual.";
    if (tLow && cLow)
      return "Preferência por solidão e baixa abertura emocional sugere praticar autorreflexão antes de compartilhar — comece escrevendo sobre emoções.";
    return "Relações e abertura emocional juntas moldam a coragem para se expor; pequenas partilhas são um bom começo.";
  }

  if (temper === "Sociabilidade" && character === "Perspectiva") {
    if (tHigh && cLow)
      return "Alta sociabilidade com baixa perspectiva pode dissolver fronteiras do eu; práticas que lembrem 'eu como contexto' ajudam a manter limites sem cortar contato.";
    if (tHigh && cHigh)
      return "Sociabilidade e boa perspectiva permitem participar sem perder o centro — use ancoragens de valor antes de interações intensas.";
    if (tLow && cLow)
      return "Baixa sociabilidade e baixa perspectiva indicam tendência a isolamento sem reflexão — exercite presença conectada (ex.: 2 minutos antes de uma conversa).";
    return "Perspectiva modula participação social tornando-a mais escolhida do que automática.";
  }

  // generic fallback
  return "A interação entre seu temperamento e esta habilidade influencia tendências comportamentais. Pequenos testes e registro ajudam a entender o que funciona para você.";
}

export function generateRecommendations(vals) {
  const recs = [];
  if (vals.discipline < 40) {
    recs.push(
      "Estabeleça uma rotina mínima: 2 minutos de planejamento diário antes de iniciar."
    );
    recs.push(
      "Use timers (Pomodoro 25/5 ou 15/5) para transformar energia em foco."
    );
  } else if (vals.discipline < 70) {
    recs.push(
      "Escolha 1 ritual fixo (ex.: revisar prioridades) e mantenha por 7 dias."
    );
  } else {
    recs.push(
      "Delegue quando possível; foque no que traz maior valor em seu dia."
    );
  }

  if (vals.mentalOpenness < 40) {
    recs.push(
      "Pratique listar 3 explicações diferentes para um problema por 3 dias."
    );
    recs.push(
      "Experimente a técnica: 'E se o contrário também fosse verdade?' por 2 minutos."
    );
  } else {
    recs.push(
      "Registre 1 insight novo por dia durante 5 dias para capitalizar a abertura mental."
    );
  }

  if (vals.emotionalOpenness < 40) {
    recs.push(
      "Exercício de 1 minuto: notar sensações no corpo sem agir sobre elas."
    );
    recs.push(
      "Nomeie a emoção: diga em voz baixa 'Estou sentindo X' quando surgir emoção forte."
    );
  } else {
    recs.push(
      "Partilhe uma dificuldade com alguém de confiança esta semana e observe o efeito."
    );
  }

  // new: perspective recommendations
  if (vals.perspective < 40) {
    recs.push(
      "Prática curta: observe seus pensamentos como se fossem nuvens por 2-3 minutos."
    );
    recs.push(
      "Exercício de 'eu como contexto': descreva uma experiência em terceira pessoa."
    );
  } else {
    recs.push(
      "Use sua perspectiva para criar checkpoints diários de 1 minuto antes de decisões importantes."
    );
  }

  if (vals.activity > 65)
    recs.push(
      "Quando a energia subir, indique 2 ações pequenas que você pode realmente concluir em 20 minutos."
    );
  if (vals.sociability > 65)
    recs.push(
      "Inclua uma atividade compartilhada (conversa, caminhada) na sua rotina semanal."
    );

  return recs;
}

export function make7DayPlan(targetKey) {
  if (targetKey === "discipline") {
    return [
      "Dia 1 — Escolha UMA pequena rotina (2 minutos) para começar: por exemplo, escrever 3 tarefas do dia.",
      "Dia 2 — Configure um lembrete/sinal físico para iniciar a rotina (alarme, post-it).",
      "Dia 3 — Use timer de 15 minutos para fazer a primeira tarefa prioritária.",
      "Dia 4 — Adapte: reduza a rotina se foi difícil ou aumente se foi fácil.",
      "Dia 5 — Compartilhe sua rotina com alguém (curto relato) — accountability aumenta adesão.",
      "Dia 6 — Reflita: o que funcionou? o que atrapalhou? anote 1 ajuste.",
      "Dia 7 — Consolide: mantenha a rotina e planeje próxima semana (mesma lógica).",
    ];
  }
  if (targetKey === "mind") {
    return [
      "Dia 1 — Registre 1 pensamento que te prendia e escreva 2 alternativas para ele.",
      "Dia 2 — Faça um exercício de perspectiva: imagine o mesmo problema visto por outra pessoa.",
      "Dia 3 — Submeta uma hipótese oposta ao seu pensamento dominante por 3 minutos.",
      "Dia 4 — Pratique desfusão: repita um pensamento em voz alta com tom de narrador.",
      "Dia 5 — Liste 3 soluções possíveis, incluindo as menos óbvias.",
      "Dia 6 — Teste uma dessas soluções em pequena escala (experimento de 1 dia).",
      "Dia 7 — Reflita e registre o que mudou na sua visão.",
    ];
  }
  if (targetKey === "perspective") {
    return [
      "Dia 1 — Prática de 2 minutos: observe pensamentos como eventos (não como você).",
      "Dia 2 — Escreva um breve diário em terceira pessoa sobre um evento emocional.",
      "Dia 3 — Faça um exercício de 'colocar-se um passo atrás' por 3 minutos antes de agir.",
      "Dia 4 — Identifique 1 pensamento recorrente e descreva-o sem julgamento.",
      "Dia 5 — Treine presença: 3 minutos de atenção plena focada na respiração.",
      "Dia 6 — Use uma âncora de valores para decidir uma pequena ação.",
      "Dia 7 — Reflita: como a distância do 'eu' afetou suas escolhas?",
    ];
  }
  // default = emotional openness (aceitação)
  return [
    "Dia 1 — Prática de 2 minutos: notar a respiração e sensações no corpo.",
    "Dia 2 — Nomear emoções: quando surgir algo intenso, diga 'Estou sentindo X'.",
    "Dia 3 — Exposição curta: aceite uma emoção desconfortável por 1 minuto sem tentar mudá-la.",
    "Dia 4 — Validação: reconheça que a emoção tem propósito (mesmo que desconfortável).",
    "Dia 5 — Ação alinhada a valores: escolha uma ação pequena apesar do desconforto.",
    "Dia 6 — Compartilhe com alguém de confiança algo que sentiu esta semana.",
    "Dia 7 — Reflita sobre progresso e planeje pequena prática para seguir.",
  ];
}

export function makeWeeklyQuestion(targetKey, archetypeLabel) {
  if (targetKey === "discipline") {
    return `Questão da semana (${archetypeLabel}): Qual pequena rotina de 2 minutos, se repetida, te faria sentir mais no controle das suas tarefas?`;
  }
  if (targetKey === "mind") {
    return `Questão da semana (${archetypeLabel}): Que pensamento você pode olhar hoje com curiosidade (em vez de convicção)? Liste 1 alternativa.`;
  }
  if (targetKey === "perspective") {
    return `Questão da semana (${archetypeLabel}): Em que situação você poderia 'dar um passo atrás' esta semana antes de agir?`;
  }
  return `Questão da semana (${archetypeLabel}): Que sentimento você evita e que pequeno passo você admite hoje para senti-lo com segurança?`;
}

export function buildPlainTextSummary(
  insights,
  tempLabels,
  charLabels,
  traits
) {
  const lines = [];
  lines.push(
    `Arquétipo: ${insights.archetype.label} — ${insights.archetype.description}`
  );
  lines.push("");
  lines.push("Temperamento (percentuais):");
  lines.push(
    `${tempLabels[0]}: ${traits[0]}%  |  ${tempLabels[1]}: ${traits[1]}%  |  ${tempLabels[2]}: ${traits[2]}%`
  );
  lines.push("");
  lines.push("Carácter (percentuais):");
  // supports 7 traits input (with perspective) or 6 traits input
  const charLineParts = [
    `${charLabels[0]}: ${traits[3]}%`,
    `${charLabels[1]}: ${traits[4]}%`,
    `${charLabels[2]}: ${traits[5]}%`,
  ];
  if (traits[6] !== undefined) {
    charLineParts.push(`${charLabels[3]}: ${traits[6]}%`);
  } else {
    charLineParts.push(`${charLabels[3]}: N/A`);
  }
  lines.push(charLineParts.join("  |  "));
  lines.push("");
  lines.push("Pontos naturais:");
  insights.temperamentStrengths.forEach((s) => lines.push(`- ${s}`));
  lines.push("");
  lines.push("Recursos treináveis:");
  insights.characterResources.forEach((r) => lines.push(`- ${r}`));
  lines.push("");
  lines.push("Integração (resumo):");
  // include a compact summary of the integration matrix
  insights.integrationMatrix.forEach((cell) => {
    lines.push(`- ${cell.temper} → ${cell.character}: ${cell.text}`);
  });
  lines.push("");
  lines.push("Mini-plano 7 dias:");
  insights.miniPlan.forEach((d, i) => lines.push(`${i + 1}. ${d}`));
  lines.push("");
  lines.push("Pergunta da semana:");
  lines.push(insights.weeklyQuestion);
  return lines.join("\n");
}
